/*
 * What the license form accepts as a product version bound (SYN-3461). The Ending version is a released version
 * (6.0.5), a whole minor release (6.0.x, up to the last 6.0 release), a whole major release (7.x.x, up to the last 7
 * release) or None. The Starting version stays a released version. The server applies the same rule (SYN-3440) and
 * works out the supported versions and the status from it.
 */

const RELEASED_VERSION = /^\d+\.\d+\.\d+$/;
const MINOR_CAP = /^\d+\.\d+\.x$/;
const MAJOR_CAP = /^\d+\.x\.x$/;

export const ENDING_VERSION_HINT = 'Use a version like 6.0.5, or 6.0.x or 7.x.x.';

export const isReleasedVersion = (value) => {
	return RELEASED_VERSION.test(String(value ?? '').trim());
};

export const isMinorCap = (value) => {
	return MINOR_CAP.test(String(value ?? '').trim());
};

export const isMajorCap = (value) => {
	return MAJOR_CAP.test(String(value ?? '').trim());
};

// None (null or blank) is a valid Ending: no cap.
export const isEndingVersion = (value) => {
	if (value === null || value === undefined || String(value).trim() === '') {
		return true;
	}

	return isReleasedVersion(value) || isMinorCap(value) || isMajorCap(value);
};

export const describeInvalidEndingVersion = (value) => {
	return `Invalid ending version: '${String(value).trim()}'. ${ENDING_VERSION_HINT}`;
};

const parts = (value) => {
	return String(value).split('.').map((part) => { return part === 'x' ? null : Number(part); });
};

const compareNumbers = (a, b) => {
	return a - b;
};

/*
 * Whether a cap reaches at least the starting version: a 6.0.x cap covers a 6.0.z start and nothing later, a 7.x.x
 * cap covers every 7 start. Without a starting version every cap is offered.
 */
export const capReachesVersion = (cap, startingVersion) => {
	if (!isReleasedVersion(startingVersion)) {
		return true;
	}

	const [capMajor, capMinor] = parts(cap);
	const [major, minor] = parts(startingVersion);

	if (capMajor !== major) {
		return capMajor > major;
	}

	return capMinor === null || capMinor >= minor;
};

const describeCap = (cap) => {
	const [major, minor] = parts(cap);

	return minor === null ? `${cap} (every ${major} release)` : `${cap} (every ${major}.${minor} release)`;
};

/*
 * The caps the released versions allow, newest last: one N.M.x per minor release and one N.x.x per major release.
 */
export const capsOf = (releases) => {
	const minors = new Set();
	const majors = new Set();

	(releases || []).filter(isReleasedVersion).forEach((release) => {
		const [major, minor] = parts(release);

		minors.add(`${major}.${minor}.x`);
		majors.add(`${major}.x.x`);
	});

	const byVersion = (a, b) => {
		const [aMajor, aMinor] = parts(a);
		const [bMajor, bMinor] = parts(b);

		return compareNumbers(aMajor, bMajor) || compareNumbers(aMinor ?? 0, bMinor ?? 0);
	};

	return [...[...minors].sort(byVersion), ...[...majors].sort(byVersion)];
};

/*
 * The Ending options: None, the released versions from the starting one on, the caps that reach it, and the value
 * the license already has when the list would not offer it (a cap whose releases are not listed), so an edit never
 * loses it. Typing another value is allowed by the field and checked by the validation.
 */
export const endingVersionOptions = (releases, startingVersion, currentValue) => {
	const released = (releases || []).filter(isReleasedVersion);
	const fromStart = isReleasedVersion(startingVersion) && released.includes(startingVersion) ? released.slice(released.indexOf(startingVersion)) : released;
	const caps = capsOf(released).filter((cap) => { return capReachesVersion(cap, startingVersion); });
	const options = [
		{ value: null, label: 'None' },
		...fromStart.map((release) => { return { value: release, label: release }; }),
		...caps.map((cap) => { return { value: cap, label: describeCap(cap) }; })
	];

	if (currentValue && isEndingVersion(currentValue) && !options.some((option) => { return option.value === currentValue; })) {
		options.push({ value: currentValue, label: isReleasedVersion(currentValue) ? currentValue : describeCap(currentValue) });
	}

	return options;
};
